// The typed payloads of the external ban's kinds (#7785 part 3). Writers: the worker's
// `external-ban/`, the api's `deps/external-bans.ts`. Stored vocabulary: a field is never renamed
// once a row carries it. No row holds author text: an id, a login snapshot, a digest, an enum.

import { z } from 'zod';
import {
  ExternalBanSourceSchema,
  ExternalDangerCategorySchema,
  ExternalItemKindSchema,
  ExternalSharedBanSourceSchema,
} from '../external-ban/vocabulary';

const RepoFullName = z.string().regex(/^[^/\s]+\/[^/\s]+$/);
const GithubNumber = z.number().int().positive();
const GithubUserId = z.number().int().positive();

const ItemRef = {
  accountId: z.string().min(1),
  repo: RepoFullName,
  itemKind: ExternalItemKindSchema,
  itemNumber: GithubNumber,
};

/**
 * `external_danger.judged`: one PAID judgement of an outsider's text. A stored verdict replayed is
 * not a row: nothing was sent. `model` and `provider` say whose answer it was.
 */
export const ExternalDangerJudgedInputSchema = z.object(ItemRef);
export const ExternalDangerJudgedOutputSchema = z.object({
  read: z.enum(['first', 'second']),
  verdict: z.enum(['clean', 'suspect', 'dangerous']),
  category: ExternalDangerCategorySchema.nullable(),
  textSha256: z.string().min(1),
  promptVersion: z.string().min(1),
  model: z.string().nullable(),
  provider: z.string().nullable(),
});

/**
 * `external_ban.added`: an author joined a list. `list: 'account'` is this account's own;
 * `list: 'shared'` is a direct entry by platform staff, which names its actor.
 */
export const ExternalBanAddedInputSchema = z.object({
  accountId: z.string().min(1),
  githubUserId: GithubUserId,
  login: z.string().nullable(),
  list: z.enum(['account', 'shared']),
  actor: z.string().nullable(),
});
export const ExternalBanAddedOutputSchema = z.object({
  source: z.union([ExternalBanSourceSchema, ExternalSharedBanSourceSchema]),
  category: ExternalDangerCategorySchema.nullable(),
  /** Merged PRs this ban queued for a second look. */
  rereviewsQueued: z.number().int().nonnegative(),
  /** Why this verdict did not count toward the shared list; `null` when it did or was not asked. */
  corroboration: z
    .enum(['counted', 'private_repo', 'custom_endpoint', 'not_paying', 'account_too_young'])
    .nullable(),
  /** A person's stated why, on a door's add; a verdict's why is its evidence. */
  reason: z.string().optional(),
});

/** `external_ban.removed`: the appeal door. The reason is the door's whole price. */
export const ExternalBanRemovedInputSchema = z.object({
  accountId: z.string().min(1),
  githubUserId: GithubUserId,
  list: z.enum(['account', 'shared']),
  actor: z.string().min(1),
});
export const ExternalBanRemovedOutputSchema = z.object({ reason: z.string().min(1).max(500) });

/**
 * `external_ban.enforced`: an item met a ban, a verdict or a limit. `action` is what became of it:
 * `closing` (the attempt, written first), `closed` (only once GitHub confirmed it), `held` (nothing
 * merges and a person decides), or `not_closed` (a repo the bot does not maintain, or an attempt that
 * did not land; the maintainer still reads this row).
 */
export const ExternalBanEnforcedInputSchema = z.object({
  ...ItemRef,
  githubUserId: GithubUserId,
});
export const ExternalBanEnforceRuleSchema = z.enum([
  'banned_author',
  'dangerous_content',
  'reads_disagree',
  'suspect_content',
  'over_author_limit',
  'over_repo_limit',
  'over_item_limit',
]);
export type ExternalBanEnforceRule = z.infer<typeof ExternalBanEnforceRuleSchema>;
export const ExternalBanEnforcedOutputSchema = z.object({
  rule: ExternalBanEnforceRuleSchema,
  action: z.enum(['closing', 'closed', 'held', 'not_closed', 'comment_failed']),
  category: ExternalDangerCategorySchema.nullable(),
  /** Which list held the author, for `banned_author`. */
  list: z.enum(['account', 'shared']).nullable(),
  reads: z.number().int().nonnegative().nullable(),
  limit: z.number().int().positive().nullable(),
});

/** `external_ban.promoted`: this account's verdict made the third unrelated witness. */
export const ExternalBanPromotedInputSchema = z.object({
  accountId: z.string().min(1),
  githubUserId: GithubUserId,
});
export const ExternalBanPromotedOutputSchema = z.object({
  category: ExternalDangerCategorySchema.nullable(),
  unrelated: z.number().int().positive(),
});

/** `external_ban.rereview_filed`: a banned author's merged PR went to triage for a second look. */
export const ExternalBanRereviewFiledInputSchema = z.object({
  accountId: z.string().min(1),
  repo: RepoFullName,
  prNumber: GithubNumber,
  githubUserId: GithubUserId,
});
export const ExternalBanRereviewFiledOutputSchema = z.object({
  rawTaskId: z.string().min(1),
  filing: z.enum(['created', 'existing', 'dropped_other_author']),
});
